import { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import type { WebTask } from '../../../lib/tasks';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useTranslation } from '../../../lib/i18n';
import { timeAgo } from '../../../lib/notification-time';
import { TASKS_PAGE } from '../../../lib/tasks-bi';
import { useTaskSidePanel } from '../task-side-panel/TaskSidePanelContext';
import { AgentDot } from '../../AgentDot';
import { OutreachKindBadge } from './OutreachKindBadge';
import { OutreachThreadModal } from './OutreachThreadModal';
import { useOutreachSiblings } from './OutreachSiblingsContext';
import { TaskDetailModal } from './TaskDetailModal';
import { taskDiscussMessage, taskResolveMessage } from '../../../lib/task-resolve-message';
import { messageDir } from '../../../lib/message-direction';
import { outreachRowTitle } from '../../../lib/outreach-rows';
import { useCancelTask } from '../../../lib/use-cancel-task';
import { taskKnownAction } from '../../../lib/task-known-action';
import { taskActions, type TaskActionKind } from '../../../lib/task-actions';
import { taskDetailQuery } from '../../../lib/use-task-detail';
import { useOpenSideChat } from '../../../lib/use-open-side-chat';
import { useAgentName } from '../../../lib/use-agent-name';
import { CancelTaskConfirm } from './CancelTaskConfirm';
import styles from './tasks.module.css';
import { Card, OverflowMenu, TaskRowBadge, Badge } from '../ui';
import { awaitsDecision, isInMotion, isNotStarted, isScheduled } from '../../../lib/agent-task-buckets';
import { workingSentence } from '../../../lib/island-quickview';
import { gatingBlocker, scheduledDateLabel } from '../../../lib/task-schedule';
import { taskGoalOrigin } from '../../../lib/task-origin';
import { TaskOriginLine } from './TaskOriginLine';
import { TaskDecisionPrompts } from './TaskDecisionPrompts';
import { SocialPostPreviewCard } from '../chat-surface/MessageRow/SocialPostPreviewCard';
import { postPreviewHoldsDecision } from '../../../lib/task-row-post-preview';
import { useCategoryRowClass, useCategoryTitleClass } from '../category-view/CategoryView';

/** The BI vocabulary shared with the detail panel and both phones — never a per-surface spelling. */
const ROW_ACTION_BI: Record<TaskActionKind, string> = {
  openBrowser: 'go_to_browsing_session',
  resolve: 'resolve_with_maestro',
  discuss: 'chat_about_task',
  followUp: 'chat_about_task',
  cancel: 'cancel_task',
};

// One card shape for EVERY bucket: the title + summary, the task's status badge, a meta line
// naming the agent (its genome dot + name) and the Space it lives in with a compact last-updated
// stamp. The whole card is the affordance — it opens the task's detail panel, where the per-task
// actions (including "Resolve with {agent}") live.
//
// The card renders per viewport (Figma 7212-26450 for mobile, 13523-227319 for desktop):
//   - DESKTOP keeps the pointer refresh — the badge pinned top-right, no chevron; the DS Card's own
//     interactive surface carries the hover/Active affordance (Active while the row's panel is open).
//   - MOBILE matches native's phone frame — a touch screen has no hover, so the badge leads the
//     column, a trailing chevron is the visible open affordance, and a hairline parts the header from
//     a stacked agent-over-Space meta. The two layouts are display-gated in tasks.module.css, so the
//     desktop DOM is untouched.
export function TaskCard({
  task,
  spaceName,
  agentName,
  spaceSlug,
  resolvePhase,
  resolveKind,
  hideOrigin,
}: {
  task: WebTask;
  spaceName?: string;
  agentName?: string;
  /** Set only while this row is being held on screen after it left its bucket — see
   *  `useResolvedTaskFarewell`. `hold` wears the farewell, `exit` plays it out. Absent for every
   *  ordinary row. */
  resolvePhase?: 'hold' | 'exit';
  /** Which farewell this is — `complete` wears the green sweep, `dissolve` a plain fade. Always set
   *  together with `resolvePhase`. */
  resolveKind?: 'complete' | 'dissolve';
  /** The slug of the task's Space — what makes its goal's route resolvable (see `lib/task-origin.ts`).
   *  Resolved by the list from the workspaces roster it already holds. */
  spaceSlug?: string;
  /** The row sits under its goal's own header row, which already names the goal. */
  hideOrigin?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const { openModal, openTaskId } = useTaskSidePanel();
  const { logPageClickBi } = useGenericBi();
  const cancel = useCancelTask(TASKS_PAGE);
  const openSideChat = useOpenSideChat();
  const agent = useAgentName();
  const queryClient = useQueryClient();
  const rowSurface = useCategoryRowClass();
  const titleCap = useCategoryTitleClass();

  // This row is holding an OPEN question at the reader. It is why `bucketOfTask` files the row under
  // Action needed whatever its status says (the backend never writes a status on the block, so a blocked
  // goal-plan task is PENDING), and the badge has to say the same thing — a row that sits in the Action
  // needed tab wearing an "In progress" chip tells two stories about itself.
  //
  // `answeredHere` is the one thing the list cannot know yet: the verdict landed a moment ago and the
  // re-read has not come back. The row stops claiming it needs the reader IMMEDIATELY, with the answer
  // and what happens next printed under it by `TaskDecisionPrompts` — never a bare chip that changed
  // colour and nothing else.
  const [answeredHere, setAnsweredHere] = useState(false);
  // The questions answered on this row, so the held post leaves with its question rather than waiting for
  // the re-read (the drill-in keeps the same list for the same reason).
  const [answeredIds, setAnsweredIds] = useState<string[]>([]);
  const onAnswered = (allAnswered: boolean, decisionId: string) => {
    setAnsweredHere(allAnswered);
    setAnsweredIds((ids) => (ids.includes(decisionId) ? ids : [...ids, decisionId]));
  };
  const awaitingDecision = awaitsDecision(task) && !answeredHere;
  const isOutreach = task.rowKind === 'outreach';
  // A PENDING task whose `notBefore` is still ahead: the agent is holding it for a date. The row stays an
  // ordinary In-progress row (it IS pending work) and reads quietly — the secondary ink, plus a neutral chip
  // carrying the date — so the live rows above it keep the reader's attention.
  const scheduled = isScheduled(task);
  // Laid out and never begun: PENDING, undated, and no agent has ever been put on it. It stays an
  // In-progress row and is still counted there — it only stops announcing that work is happening, which
  // it was doing to two goal-laid-out tasks on a real screen. Same quiet treatment as `scheduled`.
  const notStarted = isNotStarted(task);
  // "Working" only while an agent is actually executing; a released row reads "Active Task".
  const inMotion = isInMotion(task);
  // Waiting for another task to finish (`isGatedTask`), the third quiet state: the chip names the task it
  // waits for, because "Not started" on a digest that follows a check-in round answered nobody's question.
  // Scheduled wins when both apply — see the rule's own doc.
  const gating = scheduled || awaitingDecision ? undefined : gatingBlocker(task);
  const quiet = scheduled || notStarted || !!gating;
  // Where this task came from, when a goal laid it out. Named above the card and linked to the goal, so a
  // reader never has to ask why the row exists.
  const goalOrigin = taskGoalOrigin(task, spaceSlug);
  const scheduledLabel = scheduled ? scheduledDateLabel(task.postAt ?? task.notBefore, Date.now(), i18n.language || undefined) : '';
  const scheduleChip = scheduledLabel ? <Badge variant="waiting-on-a-reply" label={scheduledLabel} /> : null;
  const gatedChip = gating ? <Badge variant="waiting-on-a-reply" label={t('tasks.status.after', { title: gating.title })} className={styles.chipAfter} /> : null;
  // An outreach row is titled by who sent it — the row's subject is a person, not a piece of work.
  // With no resolvable sender it is "Incoming message", NOT the task copy: a message from someone whose
  // name we failed to resolve is neither a task nor untitled, and "Untitled task" told the reader both.
  // The phones have said `tasks.outreach.untitled` all along; web now uses the same key.
  //
  // ...EXCEPT when the outreach announces a FILE, which is not correspondence: its subject is the file,
  // so the row is titled by the file. "Message from Dafna" reads as something a person wrote and says
  // nothing about what actually arrived — the report this branch answers. The decision is
  // `outreachRowTitle`, shared 1:1 with iOS/Android `OutreachRows.rowTitle`, so the three renderers of
  // this row cannot drift and the presence gate (a notice must NAME a file) is applied in one place.
  //
  // The card deliberately grows NO separate "View file" action: tapping it opens `OutreachThreadModal`,
  // which already names the file and opens it through the one shared viewer
  // (`StackedSharedFileViewer`). A second affordance here would duplicate that, and the whole card is
  // already a DS Card rendered AS a button — nesting a button inside it is invalid HTML. So the card
  // owes the correct NAME and nothing else.
  //
  // ⚠️ This path is DORMANT today, and is written anyway. Since map-home iteration 3 no received
  // outreach row reaches any task list on any surface (`mergeOutreaches` re-derives the list without
  // them; both phones no longer carry inbound outreach rows at all), so `isOutreach` is currently
  // unreachable here. The branch lands on all three surfaces together so this card cannot become the
  // one renderer still saying "Message from …" about a file the day inbound rows return.
  const outreachTitle = isOutreach ? outreachRowTitle(task) : null;
  const title = outreachTitle
    ? outreachTitle.kind === 'file'
      ? outreachTitle.fileName
      : outreachTitle.kind === 'fileUntitled'
      ? t('tasks.outreach.sharedFileUntitled')
      : outreachTitle.kind === 'sender'
      ? t('tasks.outreach.title', { name: outreachTitle.name })
      : t('tasks.outreach.untitled')
    : (task.title ?? '').trim() || t('tasks.untitledTask');
  const summary = task.summary?.trim();
  const when = timeAgo(task.updatedDate ?? null);
  // The Space this task lives in with how fresh it is — "Pizza Milano · 2d" (Figma 13641-101528).
  const where = [spaceName, when].filter(Boolean).join(' · ');
  // A live loop is executing on this task RIGHT NOW — `runInFlight === true` ONLY. Absent means "not
  // computed" (the route stamps the unsettled slice, capped per response): the row then renders exactly
  // as it always did — no dot — never idle-with-confidence and never working. The row gains a pulsing
  // dot by the agent and — when the agent has words for it (currentAction → first unticked plan step;
  // NO title fallback here, the title IS the row) — the sentence as an extra line.
  // A row held on an OPEN QUESTION shows no live marker, however live its run says it is. The dot and
  // the sentence are the card's liveness markers (#11727 made them so, replacing a "Working now" chip),
  // and on a blocked row they claim activity that is not happening: an agent that has asked something
  // and is waiting for the answer is parked, not executing. The reader's own question is the salient
  // fact there, and a pulsing dot beside "Action needed" argues with it.
  //
  // `awaitingDecision` and not `awaitsDecision(task)`: answering HERE clears it, so the marker comes
  // back the moment the row is released rather than waiting for a frame.
  const working = !isOutreach && task.runInFlight === true && !awaitingDecision;
  const sentence = working ? workingSentence(task) : null;
  // The sentence line draws its own dot, so it is a live marker in its own right.
  const showsSentence = !!sentence && sentence.kind !== 'title';
  // The row's description line (Figma 21055:247683). A goal's plan tasks carry no summary until an agent
  // reports one, so the next open step of the plan says what the task is about — unless the live line
  // above is already saying exactly that.
  const nextStep = task.goalId ? (task.plan ?? []).find((step) => !step.completed && step.title.trim())?.title.trim() : undefined;
  // The social post the agent holds for the reader's OK, shown above the question it asks about — the same
  // look the drill-in draws. Its summary only restates the post ("Waiting on your OK to post …"), so it gives
  // way to the post itself, as on both phones (`showsCardSummary` / `agentTaskCardShowsSummary`).
  const postPreview = isOutreach ? undefined : postPreviewHoldsDecision(task.socialPostPreviewCard, task.decisionPrompts, answeredIds);
  const description = postPreview ? undefined : summary || (showsSentence ? undefined : nextStep);

  // An inbound outreach has no AgentTask behind it, so there is no task detail to open. It DOES have
  // the conversation its own agent presented it in — which is where the user reads it and replies —
  // so the row opens that instead of a modal that would have nothing to show.
  // The list's outreach rows, for the thread dialog's Previous / Next (`OutreachSiblingsContext`).
  const outreachSiblings = useOutreachSiblings();
  const openDetails = () => {
    logPageClickBi({ ...TASKS_PAGE, button_name: isOutreach ? 'open_outreach' : 'open_task', task_id: task.id });
    if (isOutreach) {
      // The thread opens IN PLACE. It used to navigate to `?conversation=<id>`, which landed on the
      // ROOT every time: an inbound outreach is delivered into a SCHEDULER conversation that is not in
      // the user's chat list, so there was nothing for the param to select.
      openModal(<OutreachThreadModal task={task} siblings={outreachSiblings} />, task.id);
      return;
    }
    openModal(<TaskDetailModal taskId={task.id} />, task.id);
  };

  const isOpen = openTaskId === task.id;

  // The row's menu offers everything the task's detail does — the same rule, so an action can never exist
  // on the panel and be missing from the row. An outreach row has no AgentTask behind it, so none apply.
  const knownAction = isOutreach ? null : taskKnownAction(task);
  const rowActions = isOutreach ? [] : taskActions(task.status, !!knownAction).all;

  // "Resolve" continues the Maestro chat the task came from. The LIST payload carries no
  // `resolveConversationId` (see `taskDetailQuery`), so the row reads the detail first rather than
  // quietly seeding a fresh chat — the context loss this CTA exists to prevent.
  // Resolve, Discuss and Follow up all continue that chat, so a task never grows a second thread.
  const openTaskChat = async (message: string) => {
    let conversationId = '';
    try {
      conversationId = (await queryClient.fetchQuery(taskDetailQuery(task.id))).resolveConversationId || '';
    } catch {
      // A failed read only decides WHICH chat opens; the handoff itself must still happen.
    }
    openSideChat(conversationId ? { kind: 'existing', conversationId, message, taskId: task.id } : { kind: 'new', message, taskId: task.id });
  };
  const onResolve = () =>
    // Through the SHARED helper, like the detail panel and the task page. This row used to hardcode
    // the bare English line, which cost it both halves of that helper: the hardening that tells the
    // model what "resolve" means on a task waiting on the owner, and localization (a raw literal in a
    // 37-locale app). The consequence was the exact defect the helper was written for — the model read
    // "resolve" as "close", answered "Done, task closed", and shut a task nobody had done.
    openTaskChat(taskResolveMessage(t, { title, status: task.status, summary: task.summary }));

  const runRowAction = (kind: TaskActionKind) => () => {
    logPageClickBi({ ...TASKS_PAGE, button_name: ROW_ACTION_BI[kind], task_id: task.id });
    switch (kind) {
      case 'openBrowser':
        if (knownAction) window.open(knownAction.url, '_blank', 'noopener,noreferrer');
        return;
      case 'resolve':
        void onResolve();
        return;
      case 'cancel':
        cancel.request({ id: task.id, title });
        return;
      default:
        void openTaskChat(taskDiscussMessage(t, title));
    }
  };

  // The menu sits OUTSIDE the Card, absolutely placed over its bottom-inline-end corner. The card is
  // itself a button element (the whole row opens the detail), and a button inside a button is invalid HTML
  // that browsers resolve unpredictably — so the row-level action has to be a sibling, layered over the
  // one corner the header's badge column already keeps clear.
  // Two distinct farewells share the `hold`/`exit` phase machine but wear different CSS: `complete`
  // keeps the green sweep + slide this class pair always drew; `dissolve` (a decision answered, not the
  // task finished — see `resolveKind`) gets a plainer fade with no colour and no confetti to match.
  const dissolving = resolveKind === 'dissolve';
  const rowClass = [
    styles.cardRow,
    rowSurface && styles.cardRowInList,
    resolvePhase && (dissolving ? styles.dissolve : styles.resolved),
    resolvePhase === 'exit' && (dissolving ? styles.dissolveExit : styles.resolvedExit),
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rowClass}>
      {goalOrigin && !hideOrigin && <TaskOriginLine origin={goalOrigin} onOpen={() => logPageClickBi({ ...TASKS_PAGE, button_name: 'open_task_goal', task_id: task.id })} />}
      <Card
        component="button"
        interactive
        // The design's Active state (Figma 11440:142849 — Skin=Sunken, State=Active): the card
        // whose details are open in the side panel STAYS pressed, so the list says which row the
        // panel belongs to. `selected` paints the surface; the same prop drives its hover.
        selected={isOpen}
        aria-expanded={isOpen}
        className={`${styles.card}${rowSurface ? ` ${rowSurface}` : ''}`}
        onClick={openDetails}
      >
        <div className={styles.cardHeader}>
          <span className={styles.cardHeading}>
            <span className={styles.badgeInline}>
              <TaskRowBadge task={task} />
              {scheduleChip}
              {gatedChip}
            </span>
            {/* An outreach row is shaped like a task on purpose (same card, same statuses, same tabs),
                which is exactly why it has to SAY it isn't one. The title alone doesn't: "Message from
                יונתן" reads just as easily as a task about a message. */}
            {isOutreach && (
              <span className={styles.kindRow}>
                <OutreachKindBadge />
              </span>
            )}
            <span className={`${styles.title}${quiet ? ` ${styles.titleLater}` : ''}${titleCap ? ` ${titleCap}` : ''}`} dir={messageDir(title)}>{title}</span>
            {description && (
              <p className={styles.summary} dir={messageDir(description)}>
                {description}
              </p>
            )}
            {sentence && sentence.kind !== 'title' && (
              <p className={styles.workingLine} dir={messageDir(sentence.text)}>
                <span className={styles.liveDot} aria-hidden />
                {sentence.text}
              </p>
            )}
          </span>
          <span className={styles.badgeCorner}>
            {gatedChip}
            {scheduleChip}
            <TaskRowBadge task={task} />
          </span>
          <ChevronRight size={24} aria-hidden className={`${styles.chevron} dir-flip`} />
        </div>
        {(agentName || where || (working && !showsSentence)) && (
          <>
            <span className={styles.divider} aria-hidden />
            <div className={styles.cardMeta}>
              {agentName && (
                <span className={styles.agentTag}>
                  {task.agentId ? <AgentDot id={task.agentId} size={15} /> : null}
                  {/* The live marker sits by the agent — it is the AGENT that is working right now. */}
                  {working ? <span className={styles.liveDot} aria-hidden /> : null}
                  <span className={styles.agentName}>{agentName}</span>
                </span>
              )}
              {agentName && where ? <span className={styles.metaSep}>|</span> : null}
              {/* The LAST-resort live mark: no agent tag (a task nobody was dispatched to, or one whose
                  agent has not resolved yet) AND no sentence (no currentAction, no open plan step). Both
                  of those draw their own dot, so a fallback beside either would be a second one; without
                  any of them the card is indistinguishable from a parked one. */}
              {!agentName && working && !showsSentence ? <span className={styles.liveDot} aria-hidden /> : null}
              {where ? <span className={styles.metaText}>{where}</span> : null}
            </div>
          </>
        )}
      </Card>
      {/* An outreach row has no AgentTask behind it, so it can be blocked on nothing. */}
      {/* Outside the Card, like the question under it: the card is a button, and the post carries its own
          controls (media viewer, See more). */}
      {postPreview && (
        <div className={styles.postPreview} data-testid="taskRowPostPreview">
          <SocialPostPreviewCard card={postPreview} />
        </div>
      )}
      {!isOutreach && <TaskDecisionPrompts task={task} origin={goalOrigin} onAnsweredChange={onAnswered} className={styles.decisionSpacing} postAt={task.socialPostPreviewCard?.scheduledAt} />}
      {rowActions.length > 0 && (
        <div className={styles.cardMenu}>
          <OverflowMenu
            ariaLabel={t('tasks.rowMenu.label', { title })}
            actions={rowActions.map((action) => ({
              key: action.kind,
              // Only the Maestro labels carry `{agent}`; i18next drops a var the string doesn't use, so
              // there is no per-kind branch to keep in step here.
              label: t(action.labelKey, { agent: agent.short }),
              destructive: action.destructive,
              onSelect: runRowAction(action.kind),
            }))}
          />
        </div>
      )}
      <CancelTaskConfirm flow={cancel} />
    </div>
  );
}
